import { Button, cn } from "@tellescope/ui";
import { TellescopeFormFieldBaseProps } from "../../types";
import { RelatedContact } from "./RelatedContact";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AddRelatedContactForm, RelatedContactFormResult } from "./AddRelatedContactForm";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { convertContactToRelatedContact } from "./convert-contact-to-related-contact";
import { convertRelatedContactToContact } from "./convert-related-contact-to-contact";

const serializeRelatedContact = ({
  fname,
  lname,
  relationship,
  dateOfBirth,
  email,
  phoneNumber,
}: RelatedContactFormResult) =>
  `${fname}${lname ?? ""}${relationship}${dateOfBirth?.toISOString() ?? ""}${email}${phoneNumber ?? ""}`;

export interface RelatedContactsFieldProps extends TellescopeFormFieldBaseProps<"Related Contacts"> {
  enduserId: string;
}

export const RelatedContactsField = ({
  title,
  value,
  onChange,
  className,
  enduserId,
  onBlur,
  error,
}: RelatedContactsFieldProps) => {
  const { t } = useTranslation("forms");
  const [isAddingContact, setIsAddingContact] = useState(!value?.length);
  const [contactBeingEdited, setContactBeingEdited] = useState<RelatedContactFormResult>();

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {!!title && <h4>{title}</h4>}
      {value?.map((contact, i) => {
        const convertedContact = convertContactToRelatedContact(contact, enduserId);
        if (!convertedContact) return null;
        if (
          contactBeingEdited &&
          serializeRelatedContact(convertedContact) === serializeRelatedContact(contactBeingEdited)
        )
          return null;
        return (
          <RelatedContact
            key={`${contact.fname}-${contact.email}-${contact.dateOfBirth ?? ""}-${i}`}
            contact={convertedContact}
            onEdit={() => {
              setContactBeingEdited(convertedContact);
            }}
            onDelete={() => {
              const newValue = value?.filter((existingContact) => {
                const existingRelatedContact = convertContactToRelatedContact(existingContact, enduserId);
                if (!existingRelatedContact) return true;

                return serializeRelatedContact(existingRelatedContact) !== serializeRelatedContact(convertedContact);
              });

              onChange(newValue);
              if (!newValue?.length) {
                setIsAddingContact(true);
              }
            }}
          />
        );
      })}
      {isAddingContact || !!contactBeingEdited ? (
        <AddRelatedContactForm
          initialContact={contactBeingEdited}
          onSave={(contact) => {
            onChange(
              contactBeingEdited
                ? value?.reduce<Partial<TellescopeModelFromName<"endusers">>[]>((acc, existingContact) => {
                    const convertedExistingContact = convertContactToRelatedContact(existingContact, enduserId);
                    if (!convertedExistingContact) return acc;

                    if (
                      serializeRelatedContact(convertedExistingContact) === serializeRelatedContact(contactBeingEdited)
                    ) {
                      const updatedContact = convertRelatedContactToContact(contact, enduserId);
                      acc.push(updatedContact);
                    } else {
                      acc.push(existingContact);
                    }
                    return acc;
                  }, []) ?? []
                : [...(value ?? []), convertRelatedContactToContact(contact, enduserId)]
            );
            setIsAddingContact(false);
            setContactBeingEdited(undefined);
          }}
          onCancel={
            value?.length
              ? () => {
                  setIsAddingContact(false);
                  setContactBeingEdited(undefined);
                }
              : undefined
          }
          onBlur={onBlur}
        />
      ) : (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setIsAddingContact(true)}>
            {t("Add Contact")}
          </Button>
        </div>
      )}
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
